import { Box } from "@mui/material";
import {
  useEffect,
  useLayoutEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import {
  SITE_QUERY,
  SITE_WRAP,
} from "src/domains/shared/constants/styles/site";
import { useReducedMotion } from "../siteKit/motion/reducedMotion";
import { revealRef } from "../siteKit/motion/reveal";
import { usePageVisible } from "../siteKit/motion/usePageVisible";
import { SectionHead } from "../siteKit/SectionHead";
import { SiteSection } from "../siteKit/SiteSection";
import { SCENE_STYLES } from "./sceneStyles";

/** 장면이 이만큼 보이면 단계를 진행한다 */
const VISIBLE_THRESHOLD = 0.15;
/** 다음 단계까지 남은 시간이 이보다 짧으면 이 시간 뒤에 넘긴다(다시 보일 때 곧바로 넘어가지 않게) */
const MIN_STEP_REMAIN_MS = 200;

export interface SceneStepText {
  k: string;
  d: string;
}

/** 장면 내용이 받는 진행 상태 */
export interface SceneContext {
  /** 장면 칸(`.lx-view`) — 장면 엔진이 캔버스를 붙인다 */
  rootRef: RefObject<HTMLDivElement | null>;
  step: number;
  previousStep: number;
  /** 이번 단계 전환은 움직임 없이 바로(다시 보일 때 처음부터) */
  snap: boolean;
  /** 화면에 보이고 탭이 보이는 동안 */
  active: boolean;
  /** 지금 보이는지 — 엔진 콜백이 렌더 시점에 읽는다 */
  activeRef: RefObject<boolean>;
  reducedMotion: boolean;
}

export interface SceneSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  lead: string;
  /** 홈 구역 머리말(글자 한 단계 크게) */
  landing?: boolean;
  stepsLabel: string;
  steps: readonly SceneStepText[];
  /** 단계마다 머무는 시간(ms) */
  durations: readonly number[];
  /** 장면 전체 설명(보조 기술) */
  viewLabel: string;
  /** 단계 목록을 오른쪽에(단면 장면) */
  reverse?: boolean;
  /** 단면 장면 칸 비율 */
  bridge?: boolean;
  children: (context: SceneContext) => ReactNode;
  /** 장면 아래 이어지는 내용(기능 행) */
  after?: ReactNode;
}

/**
 * 장면 구역(marketing-landing): 단계 탭 목록 + 실제 드론 데이터 장면 + (좁은 화면) 지금 단계 설명.
 * 단계는 보이는 동안 진행 막대를 채우며 자동으로 넘어가고, 화면에 다시 들어오면 처음부터 시작한다. 화면 밖·탭 숨김이면 멈춘다.
 */
export function SceneSection({
  id,
  eyebrow,
  title,
  lead,
  landing = false,
  stepsLabel,
  steps,
  durations,
  viewLabel,
  reverse = false,
  bridge = false,
  children,
  after,
}: SceneSectionProps) {
  const titleId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const stepsRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const barAnimationRef = useRef<Animation | null>(null);
  const visibleRef = useRef(false);
  const activeRef = useRef(false);
  const [timeline, setTimeline] = useState({
    step: 0,
    previousStep: 0,
    snap: false,
  });
  const [visible, setVisible] = useState(false);
  const pageVisible = usePageVisible();
  const reducedMotion = useReducedMotion();
  const active = visible && pageVisible;
  const { step } = timeline;

  // 장면 렌더 루프가 프레임마다 읽는 값 — 렌더 중이 아니라 커밋 뒤에 옮긴다
  useLayoutEffect(() => {
    activeRef.current = active;
  }, [active]);

  const go = (next: number, snap = false) =>
    setTimeline((current) => ({
      step: next,
      previousStep: current.step,
      snap,
    }));

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const wasVisible = visibleRef.current;
        visibleRef.current = entry.isIntersecting;
        setVisible(entry.isIntersecting);
        // 화면에 다시 들어올 때마다 처음부터
        if (entry.isIntersecting && !wasVisible) go(0, true);
      },
      { threshold: VISIBLE_THRESHOLD }
    );
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  // 단계가 바뀌면 진행 막대를 처음부터 채운다(좁은 화면은 가로 막대, 지금 탭을 가운데로)
  useEffect(() => {
    const horizontal = window.matchMedia(SITE_QUERY.tablet).matches;
    const axis = horizontal ? "scaleX" : "scaleY";
    tabRefs.current.forEach((tab) => {
      const bar = tab?.querySelector<HTMLElement>("[data-bar]");
      if (!bar) return;
      bar.getAnimations().forEach((animation) => animation.cancel());
      bar.style.transform = "";
    });
    const tab = tabRefs.current[step];
    const container = stepsRef.current;
    if (horizontal && tab && container) {
      container.scrollTo({
        left: tab.offsetLeft - (container.clientWidth - tab.clientWidth) / 2,
        behavior: reducedMotion ? "auto" : "smooth",
      });
    }
    const bar = tab?.querySelector<HTMLElement>("[data-bar]");
    barAnimationRef.current = null;
    if (!bar) return;
    if (reducedMotion) {
      bar.style.transform = `${axis}(1)`;
      return;
    }
    const animation = bar.animate(
      [{ transform: `${axis}(0)` }, { transform: `${axis}(1)` }],
      {
        duration: durations[step],
        easing: "linear",
        fill: "forwards",
      }
    );
    if (!activeRef.current) animation.pause();
    barAnimationRef.current = animation;
  }, [timeline, durations, reducedMotion, step]);

  // 보이는 동안만 막대를 흘리고, 남은 시간 뒤에 다음 단계로
  useEffect(() => {
    const animation = barAnimationRef.current;
    if (!active || reducedMotion) {
      animation?.pause();
      return undefined;
    }
    animation?.play();
    const elapsed = Number(animation?.currentTime ?? 0);
    const remain = Math.max(MIN_STEP_REMAIN_MS, durations[step] - elapsed);
    const timer = window.setTimeout(
      () => go((step + 1) % steps.length),
      remain
    );
    return () => window.clearTimeout(timer);
  }, [active, reducedMotion, timeline, durations, step, steps.length]);

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
    const backward = event.key === "ArrowLeft" || event.key === "ArrowUp";
    if (!forward && !backward) return;
    event.preventDefault();
    const next = (index + (forward ? 1 : -1) + steps.length) % steps.length;
    tabRefs.current[next]?.focus();
    go(next);
  };

  return (
    <SiteSection id={id} labelledBy={titleId}>
      <Box sx={SITE_WRAP}>
        <SectionHead
          titleId={titleId}
          eyebrow={eyebrow}
          title={title}
          lead={lead}
          center
          landing={landing}
        />
        <Box
          ref={revealRef}
          data-reveal="up"
          sx={[SCENE_STYLES.layout, reverse && SCENE_STYLES.layoutReverse]}
        >
          <Box
            ref={stepsRef}
            data-scene-steps=""
            role="tablist"
            aria-label={stepsLabel}
            sx={SCENE_STYLES.steps}
          >
            {steps.map((item, index) => (
              <Box
                component="button"
                type="button"
                role="tab"
                key={item.k}
                ref={(element: HTMLButtonElement | null) => {
                  tabRefs.current[index] = element;
                }}
                aria-selected={index === step}
                tabIndex={index === step ? 0 : -1}
                onClick={() => go(index)}
                onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) =>
                  handleKeyDown(event, index)
                }
                sx={SCENE_STYLES.step}
              >
                <i data-bar="" />
                <span data-n="">{String(index + 1).padStart(2, "0")}</span>
                <span data-k="">{item.k}</span>
                <span data-d="">{item.d}</span>
              </Box>
            ))}
          </Box>
          <Box
            ref={rootRef}
            role="img"
            data-scene-view=""
            aria-label={viewLabel}
            sx={[SCENE_STYLES.view, bridge && SCENE_STYLES.viewBridge]}
          >
            {children({
              rootRef,
              ...timeline,
              active,
              activeRef,
              reducedMotion,
            })}
          </Box>
          <Box
            component="p"
            aria-hidden="true"
            data-scene-caption=""
            sx={SCENE_STYLES.caption}
          >
            {steps[step]?.d}
          </Box>
        </Box>
        {after}
      </Box>
    </SiteSection>
  );
}
